import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { getDatefromWeekNum, myAxios, MyDate, weeknum } from "src/components/common/functions";

const allowedSearchKeys = ["status", "department", "q", "view", "date"] as const;
const allowedViews = new Set(["month", "week", "day"]);

type RawTimeOffRow = {
    data: string;
    requesterEmail: string;
    requester: string;
    status: string;
    timeOffId: string;
    department?: string;
};

function calculateEndTime(startTime?: string, duration?: number | string) {
    if (!startTime || !duration) return undefined;
    const startHr = startTime.split(":")[0];
    const endHr = parseInt(startHr, 10) + parseInt(String(duration), 10);
    if (Number.isNaN(endHr)) return undefined;
    return `${endHr > 9 ? endHr : `0${endHr}`}:${startTime.split(":")[1]}`;
}

export async function offCalendarLoader({ params, request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;

    for (const [key] of url.searchParams) {
        if (!allowedSearchKeys.includes(key as typeof allowedSearchKeys[number])) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }

    if (!url.searchParams.get("status")) {
        url.searchParams.set("status", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("department")) {
        url.searchParams.set("department", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.has("q")) {
        url.searchParams.set("q", "");
        shouldRedirect = true;
    }

    const weekRes = await myAxios({ fileName: "offCalendar/readTimeOffsWeek.php" });
    if (!weekRes) throw redirect("..");

    const minTime = weekRes.data?.minTime;
    const maxTime = weekRes.data?.maxTime;
    if (!minTime || !maxTime) {
        return { weeks: [], selectedWeek: "", activeDate: "", view: "week", assignments: [] };
    }

    const dateArray: string[] = [];
    const dateMin = new Date(String(minTime).replace(/-/g, "/"));
    const dateMax = new Date(String(maxTime).replace(/-/g, "/"));
    const latestWeek = weeknum(dateMax);
    while (dateMin < dateMax) {
        dateArray.push(weeknum(dateMax));
        dateMax.setDate(dateMax.getDate() - 7);
    }
    if (!dateArray.includes(weeknum(dateMin))) {
        dateArray.push(weeknum(dateMin));
    }

    const urlView = url.searchParams.get("view") ?? "week";
    const view = allowedViews.has(urlView) ? urlView : "week";
    if (urlView !== view) {
        url.searchParams.set("view", view);
        shouldRedirect = true;
    }

    const selectedWeek = params.week && dateArray.includes(params.week) ? params.week : latestWeek;
    const fallbackDate = getDatefromWeekNum(selectedWeek, 0) ?? "";
    const requestedDate = normalizeDate(url.searchParams.get("date")) ?? fallbackDate;
    const derivedWeek = requestedDate ? weeknum(requestedDate) : selectedWeek;
    const normalizedWeek = dateArray.includes(derivedWeek) ? derivedWeek : selectedWeek;
    const activeDate = requestedDate && dateArray.includes(derivedWeek)
        ? requestedDate
        : getDatefromWeekNum(normalizedWeek, 0) ?? fallbackDate;

    if (url.searchParams.get("date") !== activeDate) {
        url.searchParams.set("date", activeDate);
        shouldRedirect = true;
    }

    if (!params.week || params.week !== normalizedWeek || shouldRedirect) {
        throw redirect(`/OffCalendar/${normalizedWeek}?${url.searchParams.toString()}`);
    }

    const { start, end } = getRangeFromView(view, activeDate, normalizedWeek);
    if (!start || !end) throw redirect("..");

    const formData = new FormData();
    formData.append("start", start);
    formData.append("end", end);
    const res = await myAxios({ fileName: "offCalendar/readTimeOffs.php", formData });
    if (!res) throw redirect("..");

    const assignments = (res.data ?? []).flatMap((row: RawTimeOffRow) => {
        try {
            const entries = JSON.parse(row.data);
            if (!Array.isArray(entries)) return [];
            return entries.map((entry: any) => ({
                ...entry,
                requesterEmail: row.requesterEmail,
                requester: row.requester,
                status: row.status === "Re-Submitted" ? "Submitted" : row.status,
                timeOffId: row.timeOffId,
                endTime: calculateEndTime(entry.startTime, entry.hours),
                department: row.department ?? "",
            }));
        } catch {
            return [];
        }
    });

    return {
        weeks: dateArray,
        selectedWeek: normalizedWeek,
        activeDate,
        view,
        assignments,
    };
}

function normalizeDate(value: string | null) {
    if (!value) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function getRangeFromView(view: string, activeDate: string, selectedWeek: string) {
    if (view === "day") {
        return { start: activeDate, end: activeDate };
    }
    if (view === "month") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        return {
            start: new MyDate(date.getFullYear(), date.getMonth(), 1).s(),
            end: new MyDate(date.getFullYear(), date.getMonth() + 1, 0).s(),
        };
    }
    return {
        start: getDatefromWeekNum(selectedWeek, 0) ?? activeDate,
        end: getDatefromWeekNum(selectedWeek, 6) ?? activeDate,
    };
}
